import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TopTippers — small leaderboard widget showing the top 5 tip senders for a
 * recipient over the last `days` days. Anonymized names + total amount +
 * 🔥 Flame Fan badge for the #1.
 *
 * Props:
 *   recipient: 'david_lauer' | 'omnimuscle' | ... (matches /api/billing/tip)
 *   days?: number (default 30)
 *   limit?: number (default 5)
 *   className?: string
 */
export default function TopTippers({ recipient, days = 30, limit = 5, className = "" }) {
  const [tippers, setTippers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/tips/top-tippers?recipient=${encodeURIComponent(recipient)}&days=${days}&limit=${limit}`
        );
        if (cancelled) return;
        setTippers(data?.tippers || []);
      } catch {
        if (!cancelled) setTippers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipient, days, limit]);

  if (loading) return null;
  if (tippers.length === 0) return null;

  return (
    <div
      data-testid="top-tippers"
      className={`bg-[#0F0F0F] border border-zinc-900 p-5 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={14} className="text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Top Tippers · {days}d
        </span>
      </div>
      <ol className="space-y-2" data-testid="top-tippers-list">
        {tippers.map((t, i) => (
          <li
            key={`${t.display_name}-${i}`}
            data-testid={`top-tipper-${i + 1}`}
            className={
              "flex items-center justify-between gap-3 px-3 py-2 transition-colors " +
              (t.is_top ? "bg-gradient-to-r from-[#D4AF37]/15 to-transparent border-l-2 border-[#D4AF37]" : "")
            }
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={
                  "text-xs font-bold tabular-nums w-5 " +
                  (t.is_top ? "text-[#D4AF37]" : "text-neutral-500")
                }
              >
                #{i + 1}
              </span>
              <span className="text-white font-bold truncate">{t.display_name}</span>
              {t.is_top && (
                <span
                  data-testid="flame-fan-badge"
                  title="Flame Fan — top tipper this month"
                  className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]"
                >
                  <Flame size={10} /> Flame Fan
                </span>
              )}
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="text-[#D4AF37] font-bold tabular-nums">${t.total_amount.toFixed(2)}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">
                {t.tip_count} {t.tip_count === 1 ? "tip" : "tips"}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
